import React, { useContext, useEffect, useState } from "react";
import { Collapse, Dropdown } from "react-bootstrap";
import { useTranslation, withTranslation } from "react-i18next";
import {
  BiChevronDown,
  BiDotsHorizontalRounded,
  BiGitMerge,
  BiPencil,
  BiPlus
} from "react-icons/bi";


import { RegulationContext } from "../../../App";
import GeneralDisclosureService from "../../../core/service/GeneralDisclosureService";
import RiskConfigService from "../../../core/service/RiskConfigService";
import "../Standards/Topics/Topics.scss";
import GDQuestionFormPopup from "./GDQuestionFormPopup";
import useHasPermission from "../../../core/hooks/useHasPermission";
import DataSourceLinker from "../Datasource/DataSourceLinker";
import LinkDataSource from "../RiskSettings/LinkDataSource/LinkDataSource";

function CollapsibleGeneralDisclosures({ t }) {
  const { glbRegulation } = useContext(RegulationContext);
  const { k, i18n } = useTranslation();
  const [gdCats, setGDCats] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeCat, setActiveCat] = useState('');
  const [editedQuestion, setEditedQuestion] = useState(null);
  const [questionToLink, setQuestionToLink] = useState({ id: 0 });
  const { hasConfigUpdatePermission } = useHasPermission();

  useEffect(() => {
    if (glbRegulation.id) {
      setQuestions([]);
      getGDCategories(glbRegulation.id);
    }
  }, [glbRegulation.id]);


  const getGDCategories = async (standardId) => {
    try {
      const response = await GeneralDisclosureService.getStandardTypes();
      const categoryData = response.data;
      const griCategories = [];
      categoryData?.forEach(category => {
        if (!category.disclosure?.questionCount) return;

        griCategories.push({
          categoryType: category.categoryType,
          questionCount: category.disclosure.questionCount,
        });
      });
      setGDCats(griCategories);
    } catch (error) {
    }
  };


  const toggleDisclosure = (cat) => {
    setQuestions([]);
    if (activeCat === cat) {
      setActiveCat('');
    } else {
      setActiveCat(cat);
      loadDisclosureQuestions(cat);
    }
  };

  const loadDisclosureQuestions = (cat) => {
    GeneralDisclosureService.getAllQuestions(cat)
      .then((response) => {
        if (response.status !== 200) return;
        const questions = response.data.data?.filter(q => q.parent === null)
          ?.sort((a, b) => a.sortOrder - b.sortOrder);
        const transformedData = questions.map((obj) => transformObject(obj));
        setQuestions(transformedData);
      })
      .catch((error) => { });
  };

  const transformObject = (obj) => {
    const transformedObj = { ...obj };
    // Add the translated values to the main object based on languageCode
    if (obj.localizedContents && obj.localizedContents.length > 0) {
      obj.localizedContents.forEach((item) => {
        if (item.fieldName === 'question') {
          transformedObj[item.languageCode] = item.translatedValue;
        } else if (item.fieldName === 'help_text') {
          transformedObj[`${item.languageCode}_help_text`] = item.translatedValue;
        }
      });
    }
    // Recursively process the children objects
    if (obj.children && obj.children.length > 0) {
      transformedObj.children = obj.children.map((child) => transformObject(child));
    }

    return transformedObj;
  };

  const editQuestion = (ques, dId) => {
    setEditedQuestion({ question: ques, disclosureId: dId });
  };

  const handleDataSourceLink = (questionId, dataSourceId = "") => {
    setQuestionToLink({
      id: questionId,
      dataSourceId: dataSourceId
    });
  };

  const handleDataSourceUnlink = (item) => {
    RiskConfigService.unlinkDatasource(glbRegulation.id, item.id, 'gd-question')
      .then((response) => {
        if (response.status === 200) {
          loadDisclosureQuestions(activeCat);
        }
      }).catch((error) => { });
  };

  const handleDataSourceCallback = () => {
    setQuestionToLink({ id: 0 });
    loadDisclosureQuestions(activeCat);
  };

  if (questionToLink?.id > 0) {
    return (
      <LinkDataSource
        regulation={glbRegulation.id}
        paramId={questionToLink.id}
        dsId={questionToLink?.dataSourceId}
        handleDataSourceCallback={handleDataSourceCallback}
        configType={'KPI'}
        paramType={'gd-question'} />
    );
  }

  return (
    <>
      {gdCats?.length > 0 && gdCats?.map((cat, catIndex) => (
        <div className="Topics" key={`cat-${catIndex}`} id={`cat-${catIndex}`}>
          <>
            <div className="card StandardsSub_Topics_Card">
              <div className="card-body p-0">
                <div className="SST_CardBox">
                  <div className="SST_CardBox_head">
                    <div className="expand_RswBox 1">
                      <BiChevronDown onClick={(e) => toggleDisclosure(cat.categoryType)}
                        aria-controls="example-collapse-text" >
                      </BiChevronDown>
                    </div>
                    <div className="SST_view_data d-flex">
                      <h6 className="mb-0 me-2" >{cat.categoryType}</h6>
                      <span><strong>{cat.questionCount ?? 0}</strong> <BiGitMerge /></span>
                    </div>
                  </div>
                  <Collapse in={activeCat === cat.categoryType}>
                    <div className="Sub_Top_ViewCard">
                      <button className="btn btn-primary mb-2" hidden={!hasConfigUpdatePermission()} onClick={() => {
                        editQuestion({ id: 0 }, questions[0]?.disclosureId)
                      }}><BiPlus /> {t("Question.label")}
                      </button>
                      {questions?.map((question, qIndex) => (
                        <div className="row m-0">
                          <div className="col-lg-8 p-0">
                            <div className="card Sub_Top_ViewCard_inner 11" key={question.id}>
                              <div className="card-body p-2">
                                <div className="row m-0 mb-2 d-flex align-items-start justify-content-between">
                                  <div className="col-lg-9 col-md-8">
                                    <h6 className="mb-0 me-2">
                                      <span>  {qIndex + 1}. </span>
                                      {(i18n.language && question[i18n.language]) ? question[i18n.language] : question.name}
                                    </h6>
                                  </div>
                                  <div className="col-lg-3 col-md-4 d-flex align-items-center justify-content-end">
                                    <ul class="d-flex align-items-center">
                                      <DataSourceLinker item={question}
                                        openDataSourceLink={(itemId, dataSourceId) => {
                                          handleDataSourceLink(question.id, dataSourceId);
                                        }}
                                        unlinkDataSource={(item) => {
                                          handleDataSourceUnlink(item);
                                        }} />
                                    </ul>
                                    <Dropdown hidden={!hasConfigUpdatePermission()}>
                                      <Dropdown.Toggle variant="blank_outline" id="dropdown-basic">
                                        <BiDotsHorizontalRounded />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu>
                                        <Dropdown.Item
                                          href="#"
                                          onClick={() => editQuestion(question, question.disclosureId)} >
                                          <BiPencil className="me-2" /> {t("Edit.label")}
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </div>
                                </div>
                                {
                                  question.children
                                    ?.sort((a, b) => a.sortOrder - b.sortOrder)
                                    ?.map((child, childIndex) => {
                                      return (
                                        <div className="card Sub_Top_ViewCard_inner SSTView_card"
                                          key={`childQues-${childIndex}`}
                                          id={`childQues-${childIndex}`}>
                                          <div className="card-body p-2">
                                            <div className="row m-0 d-flex align-items-start justify-content-between">
                                              <div className="col-lg-9 col-md-8">
                                                <h6 className="mb-0 me-2">
                                                  <span>{childIndex + 1}. </span>
                                                  {(i18n.language && child[i18n.language]) ? child[i18n.language] : child.name}
                                                  {/* {child.name} */}
                                                </h6>
                                              </div>
                                              <div className="col-lg-3 col-md-4 d-flex align-items-center justify-content-end">
                                                <ul class="d-flex align-items-center">
                                                  <DataSourceLinker item={child}
                                                    openDataSourceLink={(itemId, dataSourceId) => {
                                                      handleDataSourceLink(child.id, dataSourceId);
                                                    }}
                                                    unlinkDataSource={(item) => {
                                                      handleDataSourceUnlink(item);
                                                    }} />

                                                </ul>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              </div>
            </div>
          </>
        </div >))
      }
      {
        editedQuestion?.question?.id >= 0 &&
        <GDQuestionFormPopup disclosureId={editedQuestion?.disclosureId}
          question={editedQuestion.question}
          saveCallback={() => {
            setEditedQuestion(null);
            loadDisclosureQuestions(activeCat);
          }} />
      }
    </>
  );
}
export default withTranslation()(CollapsibleGeneralDisclosures);
